import { I18n } from 'react-redux-i18n';
import kinks from '@turf/kinks';
import booleanContains from '@turf/boolean-contains';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';

/**
  Validate a value as required
  @function required
  @param {String} fieldName Translation key to represent the field
*/
export const required = (fieldName = '') => {
  return value =>
    (!value || (Array.isArray(value) && !value.length)) &&
    I18n.t('error.required', { fieldName: I18n.t(fieldName) });
};

/**
  Validate a value matches a pattern
  @function match
  @param {String} pattern Pattern to check against
  @param {String} fieldName Translation key to represent the field
  @param {String} patternPublic Translation key to represent the pattern
*/
export const match = (pattern, fieldName = '', patternPublic = '') => {
  return value =>
    !pattern.test(value) &&
    I18n.t('error.pattern', {
      fieldName: I18n.t(fieldName),
      pattern: I18n.t(patternPublic)
    });
};

/**
  Validate a value doesn't have more characters than the max
  @function maxLength
  @param {Number} length Max length for the value
  @param {String} fieldName Translation key to represent the field
*/
export const maxLength = (length, fieldName = '') => {
  return value =>
    value.length > length &&
    I18n.t('error.maxLength', { fieldName: I18n.t(fieldName), length });
};

/**
Validate a value has at leaset characters
@function minLength
@param {Number} length Max length for the value
@param {String} fieldName Translation key to represent the field
*/
export const minLength = (length, fieldName = '') => {
return value =>
  value.length < length &&
  I18n.t('error.minLength', { fieldName: I18n.t(fieldName), length });
};

/**
  Validate a value is a valid email
  @function email
  @param {String} fieldName Translation key to represent the field
*/
export const email = (fieldName: string) => {
  const pattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-_+.]+(?:\.[a-zA-Z0-9-]+)*$/;
  return (value: any) => !pattern.test(value) && I18n.t('error.email');
};

/**
  Validate depending on a condition
  @function conditionalValidation
  @param {Function} shouldValidate Function that returns true if it should validate
  @param {Array<Function>} validations Array of validation functions to apply
*/
export const conditionalValidation = (shouldValidate, validations) => {
  return (value, values) =>
    shouldValidate(values) &&
    validations.reduce(
      (result, validation) => result || validation(value),
      undefined
    );
};

/**
  Validate a polygon is valid (it has three or more points and it has no intersections)
  @function validPolygon
  @param {String} fieldName Translation key to represent the field
*/
export const validPolygon = (fieldName = '') => {
  return value => {
    let geometry = {
      type: 'LineString',
      coordinates: value
    };
    return (
      kinks(geometry).features.length > 0 &&
      I18n.t('error.invalidPolygon', { fieldName: I18n.t(fieldName) })
    );
  };
};

/**
  Validate point is inside a polygon
  @function pointInPolygon
  @param {String} parentField FieldName of the polygon
  @param {String} fieldName Translation key to represent the field
  @param {String} parentFieldName Translation key to represent the polygon field
*/
export const pointInPolygon = (
  parentField,
  fieldName = '',
  parentFieldName = ''
) => {
  return (value, values) => {
    let parentAccessors = parentField.split('.');
    let parentValue = parentAccessors.reduce(
      (values, accessor) => values[accessor],
      values
    );
    let parentGeometry = {
      type: 'Polygon',
      coordinates: [parentValue]
    };
    return (
      !booleanPointInPolygon(value, parentGeometry) &&
      I18n.t('error.pointNotInPolygon', {
        fieldName: I18n.t(fieldName),
        parentFieldName: I18n.t(parentFieldName)
      })
    );
  };
};

/**
  Validate polygon is inside a polygon
  @function polygonInPolygon
  @param {String} parentField FieldName of the parent polygon
  @param {String} fieldName Translation key to represent the field
  @param {String} parentFieldName Translation key to represent the parent polygon field
*/
export const polygonInPolygon = (
  parentField,
  fieldName = '',
  parentFieldName = ''
) => {
  return (value, values) => {
    let parentAccessors = parentField.split('.');
    let parentValue = parentAccessors.reduce(
      (values, accessor) => values[accessor],
      values
    );
    if (!parentValue) {
      return;
    }
    let geometry = {
      type: 'Polygon',
      coordinates: [value]
    };
    let parentGeometry = {
      type: 'Polygon',
      coordinates: [parentValue]
    };

    return (
      !booleanContains(parentGeometry, geometry) &&
      I18n.t('error.pointNotInPolygon', {
        fieldName: I18n.t(fieldName),
        parentFieldName: I18n.t(parentFieldName)
      })
    );
  };
};

export const isValid = object => {
  if (object !== undefined && object !== '') return true;
  return false;
};



// WEBPACK FOOTER //
// ./src/utils/validation/index.js